import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { GetAccountPlan, GetAccounts, GetAccountNames, GetAccountQueue } from '../../services/backendServices';
import { ACCOUNT_QUEUE_STATUS } from '../../Util/key_helper';
// Create the AccountContext to share data across components
const AccountContext = createContext();
/**
 * AccountContextProvider: A provider component that manages and supplies account-related data to its children.
 */
export const AccountContextProvider = ({ children }) => {
    const [accountData, setAccountData] = useState([]);// State to store the list of accounts
    const [isAccountLoading, setIsAccountLoading] = useState(false);   // State to track if account data is being loaded
    const [isAccountDataFetched, setIsAccountDataFetched] = useState(false);  // State to check if account data has already been fetched
    const [accountPlanOption, setAccountPlanOption] = useState(null);     // State to store formatted account plan options
    const [accountNameOption, setAccountNameOption] = useState([]);// State to store account names for dropdown selections
    const [isAccountPlanFetched, setIsAccountPlanFetched] = useState(false)
    const [isAccountQueueFetched, setIsAccountQueueFetched] = useState(false);
    const [accountQueueData, setAccountQueueData] = useState([]);



    /**  * Function to Fetches available account plans and formats them for selection options   */
    const fetchAccountPlanData = async () => {
        try {
            const res = await GetAccountPlan();
            if (res && res.status === true && Array.isArray(res.data)) {

                // Format the account plan options
                const formattedOptions = res.data.map(item => ({
                    value: item.duration,
                    label: item.plan_label
                }));
                setAccountPlanOption(formattedOptions);
                setIsAccountPlanFetched(true)
            } else {
                setAccountPlanOption([]); // fallback to empty array if invalid data
            }
        } catch (error) {
            console.error("Failed to fetch account plans:", error.message);
            setAccountPlanOption([]);
        }
    };

    // Function the Fetches all accounts 
    const fetchAccountData = async () => {
        setIsAccountLoading(true);
        try {
            const res = await GetAccounts();
            if (res.status) {
                setAccountData(res.data);
                setIsAccountDataFetched(true);
            }
        } catch (e) {
            console.error("Error fetching Account:", e);
        } finally {
            setIsAccountLoading(false);
        }
    };
    // Function the fetches account queue data
    const fetchAccountQueueData = async () => {
        try {
            const res = await GetAccountQueue();
            if (res.status) {
                setAccountQueueData(res.data);
                setIsAccountQueueFetched(true);
            }
        } catch (e) {
            console.error("Error fetching Account Queue Data:", e);
        }
    };
    useEffect(() => {
        if (!isAccountQueueFetched) {
            fetchAccountQueueData();
        }
    }, [isAccountQueueFetched])
    /**
     * Removes a queue item by its ID
     * @param {number} queueId - The ID of the queue item to remove
     */
    const removeQueueData = (queueId) => {
        setAccountQueueData((prev) => prev.filter((item) => item.id !== queueId));
    };
    /**
     * Updates the status of a queue item by its ID
     * @param {number} queueId - The ID of the queue item to update
     * @param {string} newStatus - The new status to set for the queue item
     */
    // Function to update the status of a queue item
    const updateQueueStatus = useCallback((queueId, newStatus, newRemark) => {
        setAccountQueueData((prev) =>
            prev.map((item) =>
                item.id === queueId ? { ...item, status: newStatus, remark: newRemark } : item
            )
        );
    }, []);
    // Function the Fetches all accounts  namesto be used for dropdown selections
    const fetchAccountNames = async (only_my_brokers = false) => {
        try {
            const res = await GetAccountNames({ only_my_brokers: only_my_brokers });
            if (res.status) {
                const accountOptions = res.data.map((account) => ({
                    value: account.broker_id,
                    label: account.broker_combine_name,
                }));
                setAccountNameOption(accountOptions);
            }
        } catch (error) {
            console.error("Error fetching account names:", error);
        }
    };


    /**
     * Adds new account data to the existing list
     * @param {Object} newData - New account data to add
     */
    const addAccountData = (newData) => {
        setAccountData(prevData => [...prevData, newData]);
    };

    /**
      * Updates an existing account's full data by broker_id
      * @param {Object} updatedData - Updated account data
      */
    const updateAccountData = (updatedData) => {
        setAccountData((prevData) =>
            prevData.map((item) =>
                item.broker_id === updatedData.broker_id ? { ...item, ...updatedData } : item
            )
        );
    };
    /**
       * Updates an existing account's full data by broker_id
       * @param {Object} updatedData - Updated account data
       */
    const updateAccountInfo = (broker_id, updatedAccountInfo) => {
        setAccountData((prevData) =>
            prevData.map((item) =>
                item.broker_id === broker_id
                    ? {
                        ...item,
                        account_info: {
                            ...item.account_info,
                            ...updatedAccountInfo
                        }
                    }
                    : item
            )
        );
    };
    /**
       * Updates specific fields of an account by broker_id
       * @param {Int} brokerId - Broker ID to match
       * @param {Object} updatedFields - Specific fields to update
       */
    const updateAccountField = (brokerId, updatedFields) => {
        setAccountData((prevData) =>
            prevData.map((item) =>
                item.broker_id === brokerId ? { ...item, ...updatedFields } : item
            )
        );
    };

    /**
     * Deletes an account from the list by broker_id
     * @param {Int} broker_id - ID of the account to delete
     */
    const deleteAccount = (broker_id) => {
        setAccountData((prevData) => prevData.filter((item) => item.broker_id !== broker_id));
    };

    //Refreshes account data and shows a toast notification
    const handleRefresAccountData = async () => {
        try {
            await fetchAccountData();
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    // Check if a broker is currently renewing
    /*    * Checks if a broker is currently renewing an account
        * @param {Int} brokerId - ID of the broker to check
        * @returns {boolean} - True if the broker is renewing, false otherwise
        * */
    // Function to check if a broker is renewing an account
    const isBrokerRenewing = useCallback(
        (brokerId) =>
            accountQueueData.some(
                (item) =>
                    item.broker_id === brokerId &&
                    item.status === ACCOUNT_QUEUE_STATUS.RENEWING
            ),
        [accountQueueData]
    );

    // Function to check if a account is connecting an subuser account
    const isSubUserAccountInQueue = useCallback(
        (subUserId) =>
            accountQueueData.some(
                (item) =>
                    item.sub_user_id === subUserId
            ),
        [accountQueueData]
    );

    return (
        // Provide account data and functions to child components
        <AccountContext.Provider
            value={{
                accountPlanOption, // Formatted account plan options
                accountData, // List of connected account data
                isAccountLoading, // Loading status
                isAccountDataFetched, // Data fetched status
                setIsAccountDataFetched, // Function to manually set fetched status
                addAccountData, // Function to add a new account
                updateAccountData, // Function to update an existing account
                fetchAccountData, // Function to fetch account data
                updateAccountField, // Function to update specific fields of an account
                deleteAccount, // Function to delete an account
                handleRefresAccountData, // Function to refresh account data
                fetchAccountNames, // Function to fetch account names
                accountNameOption, // Options for account name dropdown
                fetchAccountPlanData,
                isAccountPlanFetched,
                fetchAccountQueueData,
                isAccountQueueFetched,
                accountQueueData,
                setIsAccountQueueFetched,
                setAccountQueueData,
                removeQueueData, updateQueueStatus,
                isBrokerRenewing, isSubUserAccountInQueue, updateAccountInfo
            }}
        >
            {children}
        </AccountContext.Provider>
    );
};

// Custom hook to easily access AccountContext in other components
export const useAccountContext = () => {
    return useContext(AccountContext);
};
